import { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, useMediaQuery } from '@mui/material';

import { lightTheme, darkTheme } from './theme';
import { StoreContext, rootStore, useAuthStore } from './stores/root_store';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { LoginPage } from './pages/auth/LoginPage';
import { PasswordResetRequestPage } from './pages/auth/PasswordResetRequestPage';
import { PasswordSetPage } from './pages/auth/PasswordSetPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { AppLayout } from './components/layout/AppLayout';
import { RequestListPage } from './pages/requests/RequestListPage';
import { RequestCreatePage } from './pages/requests/RequestCreatePage';
import { RequestDetailPage } from './pages/requests/RequestDetailPage';
import { RecurringListPage } from './pages/requests/RecurringListPage';
import { AssistantDashboard } from './pages/assistant/AssistantDashboard';
import { AssistantRequestDetail } from './pages/assistant/AssistantRequestDetail';
import AssistantRequestCalendarPage from './pages/assistant/AssistantRequestCalendarPage';
import { PaymentHistoryPage } from './pages/assistant/PaymentHistoryPage';
import { AssistantStatsPage } from './pages/assistant/AssistantStatsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { SettingsOverview } from './pages/settings/SettingsOverview';
import { SettingsEdit } from './pages/settings/SettingsEdit';
import { SettingsEmail } from './pages/settings/SettingsEmail';
import { SettingsPayments } from './pages/settings/SettingsPayments';
import { SettingsBilling } from './pages/settings/SettingsBilling';
import { SettingsCredits } from './pages/settings/SettingsCredits';
import { SettingsPayouts } from './pages/settings/SettingsPayouts';
import { SettingsAppearance } from './pages/settings/SettingsAppearance';
import { SettingsPassword } from './pages/settings/SettingsPassword';
import {
  ManagerPage,
  ManagerUserPage,
  ManagerUserOverview,
  ManagerUserEdit,
  ManagerUserPayments,
  ManagerUserAssistant,
  ManagerUserClosed,
  ManagerUserDwolla,
  ManagerUserRequests,
  TaskPaymentPage,
  MetricsDashboard,
  PaymentBatchHistoryPage,
  PaymentBatchDetailPage,
  PaidTasksPage,
  ManagerRatingsPage,
  RecurringRunLogPage,
  ManagerDeclinesPage,
  ManagerTeamsPage,
  ManagerTeamPage,
  ManagerTeamOverview,
  ManagerTeamInvites,
  ManagerTeamPayments,
  ManagerTeamBilling,
  ManagerApplicationsPage,
  ManagerApplicationDetail,
} from './pages/manager';
import { ApplyPage } from './pages/apply/ApplyPage';
import { ApplyIntroPage } from './pages/apply/ApplyIntroPage';
import { AdminJobsPage } from './pages/admin';
import { MarketingLayout } from './components/marketing/MarketingLayout';
import { ScrollToTop } from './components/ScrollToTop';
import { postAuthDestination } from './utils/auth_redirect';
import {
  HomePage,
  PersonalAssistantsPage,
  DedicatedAssistantsPage,
  DedicatedIncludedPage,
  FaqPage,
  SchedulePage,
  PlansPage,
  PressArticlePage,
} from './pages/marketing';
import {
  FieldGuidePage,
  FieldGuideContent,
} from './pages/fieldguide';
import {
  ForumPage,
  ForumHomePage,
  ForumSectionPage,
  ForumPostPage,
  ForumCreatePost,
} from './pages/forum';
import CalendarSettingsTab from './pages/calendar/CalendarSettingsTab';
import CalendarViewTab from './pages/calendar/CalendarViewTab';
import RequestCalendarPage from './pages/calendar/RequestCalendarPage';

// Protected route wrapper
const ProtectedRoute = observer(
  ({ children }: { children: React.ReactNode }) => {
    const authStore = useAuthStore();
    const location = useLocation();

    if (!authStore.initialized) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!authStore.isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <RealtimeProvider>{children}</RealtimeProvider>;
  }
);

// Redirect assistants from /request/:key to /assistant/request/:key
const CustomerRequestRoute = observer(
  ({ children }: { children: React.ReactNode }) => {
    const authStore = useAuthStore();
    const location = useLocation();

    if (!authStore.initialized) {
      return null;
    }

    if (authStore.user?.is_beta && !authStore.roles.includes('client')) {
      const newPath = location.pathname.replace(/^\/request\//, '/assistant/request/');
      return <Navigate to={newPath + location.search} replace />;
    }

    return <>{children}</>;
  }
);

// Redirect non-assistants from /assistant/request/:key to /request/:key
const AssistantRequestRoute = observer(
  ({ children }: { children: React.ReactNode }) => {
    const authStore = useAuthStore();
    const location = useLocation();

    if (!authStore.initialized) {
      return null;
    }

    if (!authStore.user?.is_beta) {
      const newPath = location.pathname.replace(/^\/assistant\/request\//, '/request/');
      return <Navigate to={newPath + location.search} replace />;
    }

    return <>{children}</>;
  }
);

// Restrict access to assistants (is_beta) OR managers
const AssistantOrManagerRoute = observer(
  ({ children }: { children: React.ReactNode }) => {
    const authStore = useAuthStore();

    if (!authStore.initialized) {
      return null;
    }

    const isAssistant = authStore.user?.is_beta;
    const isManager = authStore.roles.includes('manager');

    if (!isAssistant && !isManager) {
      return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
  }
);

// Redirect old /forum URLs to /assistant/forum
const ForumRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/forum/, '/assistant/forum');
  return <Navigate to={newPath + location.search} replace />;
};

// Redirect old /admin URLs to /assistant
const AdminRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/admin/, '/assistant');
  return <Navigate to={newPath + location.search} replace />;
};

// Public route wrapper (redirects if already authenticated)
const PublicRoute = observer(({ children }: { children: React.ReactNode }) => {
  const authStore = useAuthStore();
  const location = useLocation();

  if (!authStore.initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (authStore.isAuthenticated) {
    return <Navigate to={postAuthDestination(location, authStore.isAssistant)} replace />;
  }

  return <>{children}</>;
});

// App content with routing
const AppContent = observer(() => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      await rootStore.initialize();
      setInitialized(true);
    };
    init();
  }, []);

  if (!initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public Marketing Routes */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/personal-assistants" element={<PersonalAssistantsPage />} />
        <Route path="/dedicated-assistant" element={<DedicatedAssistantsPage />} />
        <Route path="/dedicated-included" element={<DedicatedIncludedPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/press/:slug" element={<PressArticlePage />} />
        <Route path="/dedicated-assistant/schedule" element={<SchedulePage />} />
        <Route path="/apply" element={<ApplyIntroPage />} />
      </Route>

      {/* Legacy assistant application URL */}
      <Route path="/job/survey" element={<Navigate to="/apply" replace />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/password/reset"
        element={
          <PublicRoute>
            <PasswordResetRequestPage />
          </PublicRoute>
        }
      />
      {/* Link from the reset email; not PublicRoute so it works while signed in */}
      <Route path="/password/set/:key/:uuid" element={<PasswordSetPage />} />
      {/* Link from the alternate-address confirmation email; works signed in or out */}
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Customer routes */}
        <Route path="/dashboard" element={<RequestListPage />} />
        <Route path="/apply/:step" element={<ApplyPage />} />
        <Route path="/request" element={<RequestListPage />} />
        <Route path="/request/new" element={<RequestCreatePage />} />
        <Route path="/request/recurring" element={<RecurringListPage />} />
        <Route path="/request/:requestKey" element={<CustomerRequestRoute><RequestDetailPage /></CustomerRequestRoute>} />
        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />}>
          <Route index element={<SettingsOverview />} />
          <Route path="edit" element={<SettingsEdit />} />
          <Route path="email" element={<SettingsEmail />} />
          <Route path="calendar" element={<CalendarSettingsTab />} />
          <Route path="appearance" element={<SettingsAppearance />} />
          <Route path="password" element={<SettingsPassword />} />
          <Route path="payments" element={<SettingsPayments />} />
          <Route path="billing" element={<SettingsBilling />} />
          <Route path="credits" element={<SettingsCredits />} />
          <Route path="payouts" element={<SettingsPayouts />} />
        </Route>
        {/* Calendar routes */}
        <Route path="/calendar" element={<CalendarViewTab />} />
        <Route path="/calendar/settings" element={<Navigate to="/settings/calendar" replace />} />
        <Route path="/request/:requestKey/calendar" element={<RequestCalendarPage />} />
        {/* Assistant routes */}
        <Route path="/assistant" element={<Navigate to="/assistant/available" replace />} />
        <Route path="/assistant/available" element={<AssistantDashboard />} />
        <Route path="/assistant/mine" element={<AssistantDashboard />} />
        <Route path="/assistant/mentor" element={<AssistantDashboard />} />
        <Route path="/assistant/manager" element={<AssistantDashboard />} />
        <Route path="/assistant/closed" element={<AssistantDashboard />} />
        <Route path="/assistant/payments" element={<PaymentHistoryPage />} />
        <Route path="/assistant/stats" element={<AssistantOrManagerRoute><AssistantStatsPage /></AssistantOrManagerRoute>} />
        <Route path="/assistant/request/:requestKey" element={<AssistantRequestRoute><AssistantRequestDetail /></AssistantRequestRoute>} />
        <Route path="/assistant/request/:requestKey/calendar" element={<AssistantRequestRoute><AssistantRequestCalendarPage /></AssistantRequestRoute>} />
        {/* Field Guide routes - assistant/manager only */}
        <Route path="/assistant/fieldguide" element={<AssistantOrManagerRoute><FieldGuidePage /></AssistantOrManagerRoute>}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path=":guideType" element={<FieldGuideContent />} />
          <Route path=":guideType/:entrySlug" element={<FieldGuideContent />} />
        </Route>
        {/* Forum routes - assistant/manager only */}
        <Route path="/assistant/forum" element={<AssistantOrManagerRoute><ForumPage /></AssistantOrManagerRoute>}>
          <Route index element={<ForumHomePage />} />
          <Route path=":sectionSlug" element={<ForumSectionPage />} />
          <Route path=":sectionSlug/new" element={<ForumCreatePost />} />
          <Route path=":sectionSlug/:postSlug" element={<ForumPostPage />} />
        </Route>
        {/* Redirect old /forum URLs */}
        <Route path="/forum/*" element={<ForumRedirect />} />
        <Route path="/forum" element={<ForumRedirect />} />
        {/* Manager routes */}
        <Route path="/manager" element={<ManagerPage />} />
        <Route path="/manager/metrics" element={<MetricsDashboard />} />
        <Route path="/manager/ratings" element={<ManagerRatingsPage />} />
        <Route path="/manager/task-payment" element={<TaskPaymentPage />} />
        <Route path="/manager/task-payment/history" element={<PaymentBatchHistoryPage />} />
        <Route path="/manager/task-payment/history/:runId" element={<PaymentBatchDetailPage />} />
        <Route path="/manager/task-payment/paid" element={<PaidTasksPage />} />
        <Route path="/manager/recurring-log" element={<RecurringRunLogPage />} />
        <Route path="/manager/declines" element={<ManagerDeclinesPage />} />
        <Route path="/manager/applications" element={<ManagerApplicationsPage />} />
        <Route path="/manager/applications/:tab" element={<ManagerApplicationsPage />} />
        <Route path="/manager/application/:sampleUuid" element={<ManagerApplicationDetail />} />
        <Route path="/manager/teams" element={<ManagerTeamsPage />} />
        <Route path="/manager/teams/:teamKey" element={<ManagerTeamPage />}>
          <Route index element={<ManagerTeamOverview />} />
          <Route path="invites" element={<ManagerTeamInvites />} />
          <Route path="payments" element={<ManagerTeamPayments />} />
          <Route path="billing" element={<ManagerTeamBilling />} />
        </Route>
        {/* Admin routes (sudo only) */}
        <Route path="/admin/jobs" element={<AdminJobsPage />} />
        <Route path="/admin/jobs/:tab" element={<AdminJobsPage />} />
        <Route path="/manager/user/:userKey" element={<ManagerUserPage />}>
          <Route index element={<ManagerUserOverview />} />
          <Route path="edit" element={<ManagerUserEdit />} />
          <Route path="payments" element={<ManagerUserPayments />} />
          <Route path="assistant" element={<ManagerUserAssistant />} />
          <Route path="closed" element={<ManagerUserClosed />} />
          <Route path="payouts" element={<ManagerUserDwolla />} />
          <Route path="requests" element={<ManagerUserRequests />} />
        </Route>
        {/* Legacy /admin redirects */}
        <Route path="/admin/*" element={<AdminRedirect />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
});

const App = observer(() => {
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const themePreference = rootStore.theme.preference;

  const theme = useMemo(() => {
    if (themePreference === 'light') return lightTheme;
    if (themePreference === 'dark') return darkTheme;
    // 'system' - follow device settings
    return systemPrefersDark ? darkTheme : lightTheme;
  }, [themePreference, systemPrefersDark]);

  return (
    <StoreContext.Provider value={rootStore}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </StoreContext.Provider>
  );
});

export default App;
